import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { Archive, ArrowLeft, ArrowRight, AtSign, Bell, ExternalLink, FileText, GitMerge, Mail, MessageSquare, PanelRightOpen, Phone, Search } from 'lucide-react';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { stripHtmlToText } from '@/lib/utils';
import { readSourceLabel } from '@/lib/readSource';
import { toast } from '@/store/toastStore';
import { getErrorMessage } from '@/services/apiClient';
import {
  getMessageInbox,
  getMentionConversations,
  getCommLogEntry,
  markCommLogEntryRead,
  markMentionsRead,
  type CommLogEntry,
  type CommLogMention,
} from '@/services/communications.service';
import { useEffectiveFacilityIds } from '@/hooks/useGlobalFacility';
import { facilityScopeKey } from '@/lib/facilityScopeParams';
import { useIsBelow } from '@/hooks/useBreakpoint';
import { useVisualViewportVars } from '@/hooks/useVisualViewportVars';
import { OffCanvasPanel, OffCanvasRegion } from '@/components/layout/OffCanvasPanel';
import { CommLogSectionWithBoundary as CommLogSection } from '@/v5/components/CommLogSection';
import { DetailProfile } from '@/components/layout/DetailProfile';
import { DetailRightPanel } from '@/v5/components/DetailRightPanel';
import { usePatientDetail } from '@/v5/hooks/usePatientDetail';
import { usePatientPhoto } from '@/v5/hooks/usePatientPhoto';
import { useVideoCall } from '@/v5/hooks/useVideoCall';
import { useVoiceCall } from '@/v5/hooks/useVoiceCall';
import { MessageMergeDialog } from './MessageMergeDialog';
import { formatUnmatchedSender, isOrphan, patientName } from './messageNames';

const PAGE_SIZE = 25;

/** The most rows the mentions list endpoint returns in one page. */
const MENTIONS_MAX_PAGE_SIZE = 100;

type InboxFolder = 'unread' | 'read' | 'sent' | 'archived';

const INBOX_FOLDERS: { value: InboxFolder; label: string }[] = [
  { value: 'unread', label: 'Unread' },
  { value: 'read', label: 'Read' },
  { value: 'sent', label: 'Sent' },
  { value: 'archived', label: 'Archived' },
];

/**
 * A list row. In the @Mentions view it also carries `mention`, which is what
 * decides its bold (the message's own read state is practice-wide).
 */
type InboxEntry = CommLogEntry & { mention?: CommLogMention };

/** The conversation a row belongs to: the @Mentions list has one row per patient. */
function conversationKey(entry: InboxEntry): number | null | undefined {
  return entry.mention?.patientId ?? entry.patientId;
}

/** A next page of @Mentions rows, minus any conversation the list already shows. */
function withoutLoadedConversations(loaded: InboxEntry[], incoming: InboxEntry[]): InboxEntry[] {
  const seen = new Set(loaded.map(conversationKey));
  return incoming.filter((entry) => !seen.has(conversationKey(entry)));
}

/**
 * A re-read @Mentions list with the open conversation kept where it was. A
 * conversation the user just opened sorts into the read half on the server,
 * which can fall past the rows re-read; dropping it would close the thread
 * they are reading.
 */
function keepSelectedRow(fresh: InboxEntry[], previous: InboxEntry[], selected: InboxEntry | null): InboxEntry[] {
  if (!selected || fresh.some((entry) => entry.commLogId === selected.commLogId)) return fresh;
  const index = previous.findIndex((entry) => entry.commLogId === selected.commLogId);
  if (index < 0) return fresh;
  const kept = previous[index];
  const rest = fresh.filter((entry) => conversationKey(entry) !== conversationKey(kept));
  const at = Math.min(index, rest.length);
  return [...rest.slice(0, at), kept, ...rest.slice(at)];
}

const FILTER_BAR_CLASS =
  'flex items-center gap-1 border-b border-slate-200 bg-slate-50/60 px-3 py-1.5 max-md:overflow-x-auto';

/**
 * Query keys refreshed after mentions are marked read: the nav counts (and the
 * drop-down list under the same prefix), the Dashboard Inbox card and its modal,
 * and the Inbox page, all of which read the same per-user read flag.
 */
const MENTION_READ_INVALIDATIONS: readonly (readonly string[])[] = [
  ['navigation', 'badges'],
  ['inbox-counts'],
  ['dashboard-my-inbox'],
  ['dashboard-inbox-modal'],
  ['inbox-list'],
];

const MENTION_READ_FAILED_TITLE = "Couldn't mark as read";

/**
 * Whether the cached nav counts say the user has an unopened mention. Outside
 * the @Mentions view the page has no per-row mention state, so this is what
 * decides whether opening a conversation is worth a request. Other queries
 * share the ['navigation', 'badges'] prefix (the drop-down list) with another
 * shape, so only a numeric `mentions` counts.
 */
function cachedBadgesShowMentions(queryClient: QueryClient): boolean {
  return queryClient
    .getQueriesData<unknown>({ queryKey: ['navigation', 'badges'] })
    .some(([, data]) => {
      const mentions = (data as { mentions?: unknown } | null | undefined)?.mentions;
      return typeof mentions === 'number' && mentions > 0;
    });
}

function MentionsFilterBar({
  onMarkAllRead,
  disabled,
}: {
  onMarkAllRead: () => void;
  disabled: boolean;
}) {
  return (
    <div className={FILTER_BAR_CLASS}>
      <button
        type="button"
        onClick={onMarkAllRead}
        disabled={disabled}
        className="max-md:shrink-0 touch-target-h max-md:px-3.5 max-md:text-sm ml-auto rounded-full px-2.5 py-0.5 text-xs font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-600 disabled:cursor-default disabled:opacity-50 disabled:hover:bg-transparent"
      >
        Mark all as read
      </button>
    </div>
  );
}

function InboxFilterBar({
  folder,
  onChange,
  taggedToMe,
  onTaggedToMeChange,
}: {
  folder: InboxFolder;
  onChange: (next: InboxFolder) => void;
  taggedToMe: boolean;
  onTaggedToMeChange: (next: boolean) => void;
}) {
  return (
    <div className={FILTER_BAR_CLASS}>
      {INBOX_FOLDERS.map((pill) => {
        const active = pill.value === folder;
        return (
          <button
            key={pill.value}
            type="button"
            onClick={() => onChange(pill.value)}
            className={[
              // max-md:min-h-[44px] is R13's touch floor, spelled the way
              // components/ui/Button.tsx spells TOUCH_FLOOR so the two stay greppable
              // as one rule. Desktop keeps the compact pill.
              'max-md:shrink-0 touch-target-h max-md:px-3.5 max-md:text-sm rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors',
              active
                ? 'bg-slate-200 text-slate-700'
                : 'text-slate-500 hover:bg-slate-100 hover:text-slate-600',
            ].join(' ')}
          >
            {pill.label}
          </button>
        );
      })}
      {/*
        An icon, not a labelled pill: the four folder pills already fill the
        320px inbox column, and a "Tagged me" pill pushed past its right edge.
        ml-auto parks it in the corner, which also reads as "modifier", not
        "fifth folder" — it narrows whichever folder is selected rather than
        replacing it.

        AtSign is the product's existing word for this: ConversationThread
        stamps the same glyph on the "Tagged:" line of every message, and the
        recipient picker uses it to tag someone. Anyone who has seen a tag
        recognises it here.

        An icon-only control carries no label, so the ON state has to be
        unmistakable — it is hiding messages. Hence a filled accent rather
        than the folder pills' quiet grey, plus aria-pressed and a title that
        says what a click will do next.
      */}
      <button
        type="button"
        onClick={() => onTaggedToMeChange(!taggedToMe)}
        aria-pressed={taggedToMe}
        aria-label={
          taggedToMe
            ? "Showing only messages you're tagged in. Select to show all messages."
            : "Show only messages you're tagged in"
        }
        title={
          taggedToMe
            ? "Showing only messages you're tagged in — select to show all"
            : "Show only messages you're tagged in"
        }
        className={[
          'max-md:shrink-0 touch-target-h ml-auto flex items-center justify-center rounded-full p-1.5 transition-colors',
          taggedToMe
            ? 'bg-blue-600 text-white hover:bg-blue-700'
            : 'text-slate-500 hover:bg-slate-100 hover:text-slate-600',
        ].join(' ')}
      >
        <AtSign className="h-4 w-4 shrink-0" />
      </button>
    </div>
  );
}

function isInbound(entry: CommLogEntry): boolean {
  return entry.type === 'email_received' || entry.type === 'sms_received';
}

function formatInboxTimestamp(dateStr: string): string {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((startOfToday.getTime() - startOfDate.getTime()) / 86_400_000);

  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  if (diffDays === 1) {
    return 'Yesterday';
  }
  if (diffDays < 7) {
    return date.toLocaleDateString([], { weekday: 'short' });
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/**
 * "Aug 21, 2026 at 10:32 AM" for the read log. Hand-rolled like
 * formatInboxTimestamp above: the server sends a naive local-time string with
 * no offset, so it must not go through anything that reads it as UTC.
 */
function formatReadTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const day = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day} at ${time}`;
}

function messagePreview(entry: CommLogEntry): string {
  const rawPreview = entry.smsMessage?.trim() || entry.emailBody?.trim() || entry.message?.trim() || '';
  return stripHtmlToText(rawPreview) || '—';
}

function InboxTypeIcon({ entry }: { entry: CommLogEntry }) {
  const className = 'h-3.5 w-3.5 shrink-0';
  switch (entry.type) {
    case 'email_sent':
    case 'email_received':
      return <Mail className={className} />;
    case 'sms_sent':
    case 'sms_received':
    case 'chat':
      return <MessageSquare className={className} />;
    case 'call_logged':
      return <Phone className={className} />;
    case 'stage_change':
      return <ArrowRight className={className} />;
    case 'autoresponder':
      return <Bell className={className} />;
    case 'document_sent':
      return <FileText className={className} />;
    case 'note':
    default:
      return <FileText className={className} />;
  }
}

function InboxPreview({ entry, unread }: { entry: CommLogEntry; unread: boolean }) {
  const preview = messagePreview(entry);
  const textClassName = unread ? 'truncate font-bold text-slate-950' : 'truncate font-medium text-slate-600';

  return (
    <div className="mt-1 flex min-w-0 items-center gap-1.5 text-sm">
      <span className={unread ? 'text-slate-700' : 'text-slate-400'}>
        <InboxTypeIcon entry={entry} />
      </span>
      <span className={textClassName} title={preview}>
        {preview}
      </span>
    </div>
  );
}

function InboxListItem({
  entry,
  unread,
  selected,
  onSelect,
  onNameClick,
  onArchive,
  onMerge,
  mentionedBy,
}: {
  entry: InboxEntry;
  unread: boolean;
  selected: boolean;
  onSelect: (entry: InboxEntry) => void;
  onNameClick?: (entry: InboxEntry) => void;
  onArchive: (entry: InboxEntry) => void;
  onMerge: (entry: InboxEntry) => void;
  /** Who mentioned the user, on @Mentions rows. */
  mentionedBy?: string;
}) {
  // `patientPhotoUrl` is null for a photo-less patient, so the row renders its
  // placeholder without a by-id request (SCRUM-1648).
  const photoUrl = usePatientPhoto(entry.patientId ? String(entry.patientId) : null, entry.patientPhotoUrl);
  const orphan = isOrphan(entry);
  const canArchive = isInbound(entry) && Number(entry.wasRead ?? 0) === 0;

  const stop = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const rowBg = selected
    ? unread
      ? 'bg-blue-50'
      : 'bg-white'
    : unread
      ? 'bg-white hover:bg-blue-50/40'
      : 'bg-transparent hover:bg-white/70';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(entry)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(entry);
        }
      }}
      className={[
        'group relative w-full border-b border-slate-200 py-3 pl-3 pr-3 text-left transition-colors cursor-pointer',
        'border-l-[3px]',
        unread ? 'border-l-blue-500' : 'border-l-transparent',
        rowBg,
      ].join(' ')}
    >
      <div className="flex items-start gap-3">
        <div className="relative mt-0.5 shrink-0">
          <Avatar
            src={photoUrl}
            name={patientName(entry)}
            size="sm"
          />
          {unread && (
            <span
              aria-hidden="true"
              className="absolute -left-1 -top-1 h-2.5 w-2.5 rounded-full bg-blue-500 ring-2 ring-white"
            />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p
              className={[
                unread
                  ? 'min-w-0 flex-1 truncate text-sm font-bold text-slate-950'
                  : 'min-w-0 flex-1 truncate text-sm font-medium text-slate-500',
                onNameClick ? 'cursor-pointer hover:underline' : '',
              ].join(' ')}
              title={onNameClick ? 'Open patient details' : undefined}
              onClick={
                onNameClick
                  ? (e) => {
                      e.stopPropagation();
                      onNameClick(entry);
                    }
                  : undefined
              }
            >
              {patientName(entry)}
            </p>
            <span
              className={
                unread
                  ? 'shrink-0 text-xs font-semibold text-blue-600'
                  : 'shrink-0 text-xs text-slate-400'
              }
            >
              {formatInboxTimestamp(entry.createdAt)}
            </span>
          </div>

          {entry.facilityName && (
            <p className="mt-0.5 truncate text-xs text-slate-500" title={entry.facilityName}>
              {entry.facilityName}
            </p>
          )}

          {/* @Mentions rows only. The timestamp above is already the mention's. */}
          {mentionedBy && (
            <p className="mt-0.5 truncate text-xs text-slate-500" title={`Mentioned by ${mentionedBy}`}>
              Mentioned by {mentionedBy}
            </p>
          )}

          <div className="mt-1 flex items-end justify-between gap-2">
            <div className="min-w-0 flex-1">
              <InboxPreview entry={entry} unread={unread} />
            </div>
            <div className="flex shrink-0 items-center gap-0.5">
              {canArchive && (
                <button
                  type="button"
                  title="Archive (mark as read)"
                  aria-label="Archive"
                  onClick={(e) => {
                    stop(e);
                    onArchive(entry);
                  }}
                  // Both row actions sit inside a row that is itself a tap target, so
                  // below md they grow to R13's 44px square rather than staying a
                  // 22px icon a thumb cannot hit without opening the conversation.
                  className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 touch-target-44"
                >
                  <Archive className="h-3.5 w-3.5" />
                </button>
              )}
              {orphan && (
                <button
                  type="button"
                  title="Merge to patient"
                  aria-label="Merge"
                  onClick={(e) => {
                    stop(e);
                    onMerge(entry);
                  }}
                  className="rounded p-1 text-amber-500 transition-colors hover:bg-amber-50 hover:text-amber-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 touch-target-44"
                >
                  <GitMerge className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InboxList({
  items,
  selectedId,
  loading,
  loadingMore,
  hasNext,
  activeSearch,
  taggedToMe,
  mentionsEmptyMessage,
  onSelect,
  onNameClick,
  onArchive,
  onMerge,
  onLoadMore,
}: {
  items: InboxEntry[];
  selectedId: number | null;
  loading: boolean;
  loadingMore: boolean;
  hasNext: boolean;
  activeSearch?: string;
  taggedToMe?: boolean;
  /** Set in the @Mentions view: rows bold by their mention, and this is the empty text. */
  mentionsEmptyMessage?: string;
  onSelect: (entry: InboxEntry) => void;
  onNameClick?: (entry: InboxEntry) => void;
  onArchive: (entry: InboxEntry) => void;
  onMerge: (entry: InboxEntry) => void;
  onLoadMore: () => void;
}) {
  const mentionsView = mentionsEmptyMessage != null;
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
  const loadRequestPendingRef = useRef(false);

  useEffect(() => {
    if (!loadingMore) {
      loadRequestPendingRef.current = false;
    }
  }, [loadingMore]);

  useEffect(() => {
    if (!hasNext) {
      loadRequestPendingRef.current = false;
      return;
    }

    const root = scrollContainerRef.current;
    const sentinel = loadMoreSentinelRef.current;
    if (!root || !sentinel) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting || loadRequestPendingRef.current || loadingMore) {
          return;
        }

        loadRequestPendingRef.current = true;
        onLoadMore();
      },
      {
        root,
        rootMargin: '0px 0px 240px 0px',
        threshold: 0,
      },
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [hasNext, items.length, loadingMore, onLoadMore]);

  if (loading && items.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="lg" label="Loading inbox..." />
      </div>
    );
  }

  if (items.length === 0) {
    const trimmedSearch = activeSearch?.trim() ?? '';
    // Search wins: it is the narrower thing the user just typed. The tagged
    // variant says what is missing, not what fills the list — nobody needs the
    // mechanism explained back to them.
    const emptyMessage = mentionsView
      ? mentionsEmptyMessage
      : trimmedSearch
        ? `No messages match "${trimmedSearch}".`
        : taggedToMe
          ? "No messages you're tagged in."
          : 'No messages available.';
    return (
      <div className="flex h-full items-center justify-center px-6 text-center text-sm text-slate-500">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div ref={scrollContainerRef} className="min-h-0 flex-1 overflow-y-auto">
        {items.map((entry) => {
          // A mention row is a conversation, often a note, so its bold follows
          // whether the user has opened the mention, not the message's read flag.
          const unread = mentionsView
            ? Boolean(entry.mention?.unread)
            : isInbound(entry) && Number(entry.wasRead ?? 0) === 0;
          const selected = entry.commLogId === selectedId;
          return (
            <InboxListItem
              key={entry.commLogId}
              entry={entry}
              unread={unread}
              selected={selected}
              onSelect={onSelect}
              onNameClick={onNameClick}
              onArchive={onArchive}
              onMerge={onMerge}
              mentionedBy={mentionsView ? entry.mention?.mentionedByName?.trim() || undefined : undefined}
            />
          );
        })}

        {(hasNext || loadingMore) && (
          <div className="px-4 py-3">
            <div ref={loadMoreSentinelRef} className="h-1 w-full" aria-hidden="true" />
            <div className="flex min-h-10 items-center justify-center text-sm text-slate-500">
              {loadingMore ? (
                <span className="inline-flex items-center gap-2">
                  <Spinner size="sm" />
                  Loading older messages...
                </span>
              ) : (
                'Scroll down to load older messages'
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function MessagesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // The @Mentions view is a place the nav links to, not a filter that carries
  // over, so it lives in the URL rather than in state.
  const mentionsView = searchParams.get('view') === 'mentions';
  const [items, setItems] = useState<InboxEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InboxEntry | null>(null);
  const [search, setSearch] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');
  const [folder, setFolder] = useState<InboxFolder>('unread');
  // Not persisted, same as `folder`. A filter that silently hides messages on a
  // later visit gets reported as missing messages, not as a filter.
  const [taggedToMe, setTaggedToMe] = useState(false);
  const requestSeqRef = useRef(0);
  const currentView = mentionsView ? 'mentions' : 'inbox';
  // Which view `items` were last loaded for; null while a reset is waiting on
  // its load. Effects that read `items` skip until it matches the URL, so a
  // link into @Mentions is never matched against the Unread rows still shown.
  const loadedViewRef = useRef<'mentions' | 'inbox' | null>(null);
  // The @Mentions list is paged by offset over "unread first". Clearing moves a
  // conversation to the read half on the server, so once anything was cleared
  // the next page no longer follows on from the rows loaded here.
  const mentionOffsetsStaleRef = useRef(false);
  // Bumped when Mark all as read succeeds. A per-conversation clear that fails
  // after that has nothing to put back: the server has cleared it anyway.
  const mentionsClearGenRef = useRef(0);
  const selectedItemRef = useRef<InboxEntry | null>(null);
  useEffect(() => {
    selectedItemRef.current = selectedItem;
  }, [selectedItem]);

  // ── Narrow-width tiers ──────────────────────────────────────────────────────
  // Columns and the patient rail leave the layout at the same boundary, and that
  // boundary is `lg`. The tempting split is `md`, but the floor arithmetic has to
  // count the shell's own chrome: at 768 the nav rail is still 16rem (it is open by
  // default — sidebarPrefs.readNav(true)) and the patient-history rail is another
  // 56px (`hidden md:flex w-14`, always rendered). A 320px inbox then leaves the
  // conversation ~120px, which is the reported "text wraps two words per line" at a
  // width the phone tier was converted away from. Single column gets 488px there.
  //
  // JS branches rather than `max-lg:` class escapes: a printed page lays out near
  // 816px, so an escape at the `lg` boundary would fire while printing and drop the
  // patient rail out of printed output.
  //
  // `isPhone` survives for one thing only: copy that has to fit a phone-width field.
  const isPhone = useIsBelow('md');
  const singleColumn = useIsBelow('lg');
  const [detailOpen, setDetailOpen] = useState(false);

  const commitSearch = useCallback(() => {
    const trimmed = search.trim();
    // Min length 2 to avoid scanning the world for a single character.
    const next = trimmed.length >= 2 ? trimmed : '';
    setCommittedSearch((prev) => (prev === next ? prev : next));
  }, [search]);
  const requestedCommLogId = useMemo(() => {
    const value = Number(searchParams.get('commLogId'));
    return Number.isFinite(value) && value > 0 ? value : null;
  }, [searchParams]);
  const requestedPatientId = useMemo(() => {
    const value = Number(searchParams.get('patientId'));
    return Number.isFinite(value) && value > 0 ? value : null;
  }, [searchParams]);

  // A facility row on the Communication Center overview links here with
  // ?facilityId=. It narrows this page only; the global picker is not touched.
  const requestedFacilityId = useMemo(() => {
    const value = Number(searchParams.get('facilityId'));
    return Number.isInteger(value) && value > 0 ? value : null;
  }, [searchParams]);
  const requestedFacilityValues = useMemo(
    () => (requestedFacilityId ? [String(requestedFacilityId)] : undefined),
    [requestedFacilityId],
  );

  // Every facility selected in the global picker, or the linked facility when
  // the picker is empty. The hook hands back strings; the key keeps the numeric
  // list stable across renders so it can sit in deps. The picker wins over a
  // page value, so a linked facility inside a several-facility selection is
  // narrowed to here, and one outside it is not shown.
  const effectiveFacilityIds = useEffectiveFacilityIds(requestedFacilityValues);
  const facilityKey = facilityScopeKey(
    requestedFacilityId && effectiveFacilityIds.includes(String(requestedFacilityId))
      ? [requestedFacilityId]
      : effectiveFacilityIds.map(Number),
  );
  const facilityIds = useMemo(
    () => (facilityKey ? facilityKey.split(',').map(Number) : []),
    [facilityKey],
  );

  const selectedPatientId = selectedItem?.patientId ? String(selectedItem.patientId) : null;
  const { detail, loading: detailLoading } = usePatientDetail(selectedPatientId);
  const patientPhoto = usePatientPhoto(selectedPatientId, selectedItem?.patientPhotoUrl);
  const voiceCall = useVoiceCall();
  const videoCall = useVideoCall();

  const selectedPatientName = useMemo(() => {
    if (selectedItem) {
      return patientName(selectedItem);
    }
    return 'Unknown patient';
  }, [selectedItem]);

  const selectedPatientEmail = useMemo(() => {
    const detailPatient = detail.patient as Record<string, unknown> | null;
    return String(detailPatient?.email ?? selectedItem?.patientEmail ?? '').trim() || undefined;
  }, [detail.patient, selectedItem?.patientEmail]);

  const selectedPatientPhone = useMemo(() => {
    const detailPatient = detail.patient as Record<string, unknown> | null;
    // Prefer the phone carried on the SELECTED inbox item: it is always the same
    // letterID-joined patient as `selectedItem.patientId` (the addressee), so the
    // SMS destination can never drift away from who the reply is addressed to.
    // `usePatientDetail` is an async, unsequenced fetch that can lag or resolve
    // out of order while the agent clicks through conversations — trusting it
    // here previously let the destination fall back to a *previously-viewed*
    // patient's number, delivering texts to the wrong person.
    return String(
      selectedItem?.patientPhone
      ?? detailPatient?.cellPhone
      ?? detailPatient?.phone
      ?? detailPatient?.homePhone
      ?? '',
    ).trim() || undefined;
  }, [detail.patient, selectedItem?.patientPhone]);

  const selectedPatientSmsOptIn = useMemo(() => {
    const detailPatient = detail.patient as Record<string, unknown> | null;
    return String(detailPatient?.smsOptIn ?? '1') !== '0';
  }, [detail.patient]);

  /**
   * `windowSize` (@Mentions only, a multiple of PAGE_SIZE): re-read the first
   * `windowSize` rows in one request and replace the list, as the "load more"
   * that follows a clear. `nextPage` is ignored then; paging resumes after the
   * rows re-read.
   */
  const loadMessages = useCallback(async (nextPage: number, append: boolean, windowSize?: number) => {
    const refreshingWindow = windowSize != null;
    if (append || refreshingWindow) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setLoadError(null);
    }

    const seq = ++requestSeqRef.current;
    // A read from the top lines the offsets up again. A clear landing while it
    // is in flight sets this back.
    if (!append) mentionOffsetsStaleRef.current = false;

    const folderParam: 'unread' | 'read' | 'sent' =
      folder === 'archived' ? 'read' : folder;

    try {
      const response = mentionsView
        ? await getMentionConversations({
          facilityIds,
          page: refreshingWindow ? 1 : nextPage,
          pageSize: windowSize ?? PAGE_SIZE,
        })
        : await getMessageInbox(
          {
            // None or one facility: the same `facilityId` input as before
            // several were supported, so that request does not change. Only a
            // multi-facility selection sends the list.
            ...(facilityIds.length > 1 ? { facilityIds } : { facilityId: facilityIds[0] }),
            search: committedSearch || undefined,
            folder: folderParam,
            taggedToMe: taggedToMe || undefined,
          },
          { page: nextPage, pageSize: PAGE_SIZE, includeTotal: false },
        );
      if (seq !== requestSeqRef.current) return;
      const nextItems: InboxEntry[] = response.data ?? [];
      loadedViewRef.current = mentionsView ? 'mentions' : 'inbox';
      setItems((prev) => {
        if (append) {
          return [...prev, ...(mentionsView ? withoutLoadedConversations(prev, nextItems) : nextItems)];
        }
        return refreshingWindow ? keepSelectedRow(nextItems, prev, selectedItemRef.current) : nextItems;
      });
      setHasNext(Boolean(response.meta?.hasNext));
      setPage(refreshingWindow ? windowSize / PAGE_SIZE : nextPage);
    } catch (error) {
      if (seq !== requestSeqRef.current) return;
      if (!append) mentionOffsetsStaleRef.current = true;
      const message = getErrorMessage(
        error,
        mentionsView
          ? "Couldn't load your mentions. Refresh the page to try again."
          : 'Unable to load messages.',
      );
      setLoadError(message);
      toast.error('Load failed', message);
    } finally {
      if (seq === requestSeqRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [mentionsView, facilityIds, committedSearch, folder, taggedToMe]);

  useEffect(() => {
    setSelectedItem(null);
    setItems([]);
    setPage(1);
    setHasNext(false);
    loadedViewRef.current = null;
    mentionOffsetsStaleRef.current = false;
  }, [mentionsView, facilityKey, committedSearch, folder, taggedToMe]);

  // Following the nav's Unread entry while already here lands on the same URL,
  // so nothing above changes and the pills would keep showing, say, Sent under
  // an "Unread" link. A new history entry is the one signal that a link was
  // followed; the pills and the toggle never navigate, so they cannot trip it.
  // The ref starts at the mount's key, so the first render is not a "follow".
  // Only the bare /messages the Unread entry links to counts: a link carrying
  // a query (a notification's ?commLogId=, the @Mentions view) keeps the
  // folder and Tagged me the user had.
  const locationKeyRef = useRef(location.key);
  useEffect(() => {
    if (locationKeyRef.current === location.key) return;
    locationKeyRef.current = location.key;
    if (location.pathname !== '/messages' || location.search !== '') return;
    setFolder('unread');
    setTaggedToMe(false);
  }, [location.key, location.pathname, location.search]);

  useEffect(() => {
    void loadMessages(1, false);
  }, [loadMessages]);

  const invalidateMentionReaders = useCallback(() => {
    for (const queryKey of MENTION_READ_INVALIDATIONS) {
      void queryClient.invalidateQueries({ queryKey: [...queryKey] });
    }
  }, [queryClient]);

  /** Set every loaded row of `patientId` (or every row, for null) to this mention state. */
  const setMentionUnread = useCallback((patientId: number | null, unread: boolean) => {
    const patch = (entry: InboxEntry): InboxEntry => (
      entry.mention && (patientId == null || entry.mention.patientId === patientId)
        ? { ...entry, mention: { ...entry.mention, unread } }
        : entry
    );
    setItems((prev) => prev.map(patch));
    setSelectedItem((prev) => (prev ? patch(prev) : prev));
  }, []);

  /**
   * Clear the user's mentions on a conversation they deliberately opened: a row
   * click or tap, a name click, or a nav/notification link being honoured.
   * Never called for the desktop first-row auto-select — showing a conversation
   * nobody chose must not mark it as seen.
   *
   * In the @Mentions view the row says whether there is anything to clear.
   * Elsewhere rows carry no mention state, so the cached nav count stands in:
   * no request unless it shows at least one unopened mention.
   */
  const clearMentionsOnOpen = useCallback((entry: InboxEntry) => {
    const patientId = entry.mention?.patientId || entry.patientId;
    if (!patientId) return;
    if (mentionsView) {
      if (!entry.mention?.unread) return;
    } else if (!cachedBadgesShowMentions(queryClient)) {
      return;
    }

    if (mentionsView) setMentionUnread(patientId, false);
    const clearGeneration = mentionsClearGenRef.current;
    markMentionsRead({ patientId }, 'communication_center')
      .then((result) => {
        if (mentionsView) mentionOffsetsStaleRef.current = true;
        // Nothing changed on the server, so no count moved.
        if (result.updated > 0) invalidateMentionReaders();
      })
      .catch(() => {
        if (clearGeneration !== mentionsClearGenRef.current) return;
        if (mentionsView) setMentionUnread(patientId, true);
        toast.error(
          MENTION_READ_FAILED_TITLE,
          "Couldn't mark this as read. Open the conversation again to try again.",
        );
      });
  }, [invalidateMentionReaders, mentionsView, queryClient, setMentionUnread]);

  const [markingAllRead, setMarkingAllRead] = useState(false);
  const handleMarkAllMentionsRead = useCallback(async () => {
    setMarkingAllRead(true);
    try {
      await markMentionsRead({ all: true }, 'communication_center', facilityIds);
      mentionsClearGenRef.current += 1;
      mentionOffsetsStaleRef.current = true;
      setMentionUnread(null, false);
      invalidateMentionReaders();
    } catch {
      toast.error(
        MENTION_READ_FAILED_TITLE,
        "Couldn't mark your mentions as read. Select Mark all as read to try again.",
      );
    } finally {
      setMarkingAllRead(false);
    }
  }, [facilityIds, invalidateMentionReaders, setMentionUnread]);
  const hasUnreadMention = mentionsView && items.some((entry) => entry.mention?.unread);

  /**
   * Whether the current deep link has already been honoured. Reset when the requested
   * ids change, so following a second link — a different notification, say — still
   * opens its conversation rather than being swallowed by the first link's latch.
   */
  const deepLinkAppliedRef = useRef(false);
  useEffect(() => {
    deepLinkAppliedRef.current = false;
  }, [requestedCommLogId, requestedPatientId]);

  useEffect(() => {
    // A deep link opens its target once, and is then spent.
    //
    // `?commLogId=` is how a notification or a patient link enters this page, and the
    // query string survives everything that happens afterwards — nothing clears it.
    // Without this latch the branch below is not a one-time hand-off but a standing
    // instruction: `selectedItem` is in this effect's dependency list, so pressing Back
    // (which sets it to null) re-runs the effect, the inequality is true again, and the
    // thread the user just dismissed re-opens on the next tick. That is the same
    // "fights its own back button" defect the auto-select below is gated for, arriving
    // by the entry path that is hardest to notice in a test — the suite renders this
    // page with no query string at all, so it passes either way.
    //
    // Deliberately a ref rather than a `singleColumn` gate: honouring the deep link is
    // right on every tier, including a phone. It is honouring it *twice* that is wrong.
    // Desktop is unaffected either way, since nothing there ever clears the selection.
    if (loading || loadedViewRef.current !== currentView) return;

    if (!deepLinkAppliedRef.current) {
      if (requestedCommLogId != null) {
        const matchedByMessage = items.find((entry) => entry.commLogId === requestedCommLogId);
        if (matchedByMessage && selectedItem?.commLogId !== matchedByMessage.commLogId) {
          deepLinkAppliedRef.current = true;
          setSelectedItem(matchedByMessage);
          // Following a link to a conversation is opening it on purpose. The
          // latch above is what keeps this to once per link.
          clearMentionsOnOpen(matchedByMessage);
          return;
        }
      }

      if (requestedPatientId != null) {
        const matchedByPatient = items.find((entry) => entry.patientId === requestedPatientId);
        if (matchedByPatient && selectedItem?.commLogId !== matchedByPatient.commLogId) {
          deepLinkAppliedRef.current = true;
          setSelectedItem(matchedByPatient);
          clearMentionsOnOpen(matchedByPatient);
          return;
        }
      }
    }

    // Opening the first row by default is right for the tier that shows a list and
    // a thread side by side, where the thread panel would otherwise sit empty. In
    // single column the thread *is* the screen, so the same line would open a
    // conversation nobody tapped — and re-open it the instant Back was pressed.
    // Never in the @Mentions view: there, opening a conversation is what marks
    // the mention as seen, so it has to be the user's choice. This automatic
    // open never clears a mention in any view.
    if (!selectedItem && items.length > 0 && !singleColumn && !mentionsView) {
      setSelectedItem(items[0]);
      return;
    }

    if (selectedItem && !items.some((entry) => entry.commLogId === selectedItem.commLogId)) {
      // In single column the selection is *which screen you are on*, so re-pointing
      // it would swap the conversation under the reader. Fall back to the list.
      setSelectedItem(singleColumn || mentionsView ? null : (items[0] ?? null));
    }
  }, [clearMentionsOnOpen, currentView, items, loading, mentionsView, requestedCommLogId, requestedPatientId, selectedItem, singleColumn]);

  const deepLinkFetchedRef = useRef(false);
  useEffect(() => {
    if (deepLinkFetchedRef.current) return;
    if (loading || items.length === 0) return;
    if (loadedViewRef.current !== currentView) return;
    if (!requestedCommLogId) return;
    const alreadyFound = items.some((e) => e.commLogId === requestedCommLogId)
      || (requestedPatientId && items.some((e) => e.patientId === requestedPatientId));
    if (alreadyFound) return;

    deepLinkFetchedRef.current = true;
    getCommLogEntry(requestedCommLogId).then((entry) => {
      if (entry) setSelectedItem(entry);
    }).catch(() => {});
  }, [currentView, loading, items, requestedCommLogId, requestedPatientId]);

  const [mergeTarget, setMergeTarget] = useState<CommLogEntry | null>(null);
  const [panelExpandTrigger, setPanelExpandTrigger] = useState(0);

  const handleSelectMessage = useCallback((entry: InboxEntry) => {
    setSelectedItem(entry);
    clearMentionsOnOpen(entry);
  }, [clearMentionsOnOpen]);

  // Clicking the sender's name selects the message and expands the detail panel.
  // Inline that is the expand trigger; off-canvas it is the sheet, and the trigger
  // is a deliberate no-op there (see v5/components/DetailRightPanel).
  const handleNameClick = useCallback((entry: InboxEntry) => {
    setSelectedItem(entry);
    clearMentionsOnOpen(entry);
    setPanelExpandTrigger((n) => n + 1);
    if (singleColumn) setDetailOpen(true);
  }, [clearMentionsOnOpen, singleColumn]);

  const handleExpandDetail = useCallback(() => {
    setPanelExpandTrigger((n) => n + 1);
    if (singleColumn) setDetailOpen(true);
  }, [singleColumn]);

  // A panel that is back in the flow must not also be open off-canvas, and a sheet
  // must not outlive the selection it is describing.
  useEffect(() => {
    if (!singleColumn || !selectedPatientId) setDetailOpen(false);
  }, [singleColumn, selectedPatientId]);

  // Only while a thread — and therefore a composer — is on screen. The keyboard does
  // not shrink the layout viewport, so a page sized in `dvh` keeps its pre-keyboard
  // height and the composer ends up underneath the keyboard. This is the P6 trap in
  // docs/mobile-responsive-migration-log.md ("the footer sat 137px below the fold").
  // Gated on the single-column tier, not on `isPhone`. The thread and its composer are
  // the whole screen all the way to 1023.98px, so the keyboard covers the composer in
  // the 768-1023 band too — the same mismatch SCRUM-1364 found for touch targets:
  // match the gate to where the component is actually the rendered instance.
  useVisualViewportVars(singleColumn && selectedItem != null);

  const handleArchiveMessage = useCallback(async (entry: CommLogEntry) => {
    if (!isInbound(entry) || Number(entry.wasRead ?? 0) !== 0) return;

    try {
      // The response carries the re-read entry, including the read log this
      // action just added to. Merging it in shows the new line straight away
      // instead of leaving it until something else refetches the message.
      const updated = await markCommLogEntryRead(entry.commLogId, 'communication_center');
      const patch = updated ? { ...updated, wasRead: 1 } : { wasRead: 1 };
      setItems((prev) => prev.map((item) => (
        item.commLogId === entry.commLogId ? { ...item, ...patch } : item
      )));
      setSelectedItem((prev) => (prev?.commLogId === entry.commLogId ? { ...prev, ...patch } : prev));
      queryClient.invalidateQueries({ queryKey: ['navigation', 'badges'] });
    } catch (error) {
      toast.error('Archive failed', getErrorMessage(error, 'Unable to mark message as read.'));
    }
  }, [queryClient]);

  const handleOpenMerge = useCallback((entry: CommLogEntry) => {
    setMergeTarget(entry);
  }, []);

  const handleMergeComplete = useCallback((updated: CommLogEntry) => {
    setItems((prev) => prev.map((item) => (
      item.commLogId === updated.commLogId ? { ...item, ...updated } : item
    )));
    setSelectedItem((prev) => (prev?.commLogId === updated.commLogId ? { ...prev, ...updated } : prev));
    void loadMessages(1, false);
  }, [loadMessages]);

  const handleLoadMore = useCallback(() => {
    if (loadingMore || !hasNext) return;
    if (mentionsView && mentionOffsetsStaleRef.current) {
      // A clear re-sorted the list on the server, so "the next page" would skip
      // or repeat conversations. Re-read from the top, one page further than
      // what was loaded, up to the most one request can return.
      const windowSize = Math.min(MENTIONS_MAX_PAGE_SIZE, (page + 1) * PAGE_SIZE);
      void loadMessages(1, false, windowSize);
      return;
    }
    void loadMessages(page + 1, true);
  }, [hasNext, loadMessages, loadingMore, mentionsView, page]);

  // In the @Mentions view the selected row is the conversation's latest mention,
  // so that is the message to point at once the user picks a row; until then,
  // the one a nav link asked for.
  const highlightMessageId = mentionsView && selectedItem?.mention
    ? String(selectedItem.commLogId)
    : requestedCommLogId ? String(requestedCommLogId) : undefined;

  const threadContent = selectedPatientId ? (
    <CommLogSection
      patientId={selectedPatientId}
      patientName={selectedPatientName}
      patientPhone={selectedPatientPhone}
      patientEmail={selectedPatientEmail}
      patientPhoto={patientPhoto}
      smsOptIn={selectedPatientSmsOptIn}
      voiceCall={voiceCall}
      videoCall={videoCall}
      highlightMessageId={highlightMessageId}
      onPatientNameClick={handleExpandDetail}
      readSource="communication_center"
    />
  ) : selectedItem && isOrphan(selectedItem) ? (
    <UnmatchedMessagePane entry={selectedItem} onMerge={() => handleOpenMerge(selectedItem)} />
  ) : mentionsView ? (
    <div className="flex h-full items-center justify-center px-6 text-center">
      <div>
        <p className="text-lg font-semibold text-slate-900">Select a conversation</p>
        <p className="mt-2 text-sm text-slate-500">Select a conversation to see where you were mentioned.</p>
      </div>
    </div>
  ) : (
    <div className="flex h-full items-center justify-center px-6 text-center">
      <div>
        <p className="text-lg font-semibold text-slate-900">Select a message</p>
        <p className="mt-2 text-sm text-slate-500">Choose a message on the left to open the patient conversation thread.</p>
      </div>
    </div>
  );

  // Built once and mounted in exactly one place — the inline rail at ≥1024, the
  // sheet below it — so the two branches cannot drift into showing different
  // fields. A panel that shows less off-canvas than inline is a bug (KTD12).
  const detailPanel = (
    <DetailRightPanel defaultCollapsed expandTrigger={panelExpandTrigger}>
      {selectedPatientId ? (
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div className="min-w-0">
              <span className="block truncate text-sm font-semibold text-foreground">
                {selectedPatientName}
              </span>
              <span className="text-xs text-muted-foreground">
                {selectedPatientEmail || selectedPatientPhone || 'Patient'}
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {selectedPatientPhone && (
                <button
                  type="button"
                  className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
                  title="Call"
                  onClick={() => window.open(`tel:${selectedPatientPhone}`)}
                >
                  <Phone className="h-4 w-4" />
                </button>
              )}
              {selectedPatientEmail && (
                <button
                  type="button"
                  className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
                  title="Email"
                  onClick={() => window.open(`mailto:${selectedPatientEmail}`)}
                >
                  <Mail className="h-4 w-4" />
                </button>
              )}
              <button
                type="button"
                className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
                title="Open patient"
                onClick={() => navigate(`/patients/${selectedPatientId}`)}
              >
                <ExternalLink className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1">
            <DetailProfile
              detail={detail}
              loading={detailLoading}
              onSectionNavigate={(section) =>
                selectedPatientId && navigate(`/patients/${selectedPatientId}?section=${section}`)
              }
            />
          </div>
        </div>
      ) : (
        <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
          No patient selected
        </div>
      )}
    </DetailRightPanel>
  );

  // Below `lg` the patient rail is a sheet and the list is off screen, so the
  // thread needs its own bar: it carries the only route back to the list and the
  // only control that reaches the rail.
  const threadBar = singleColumn && selectedItem ? (
    // `touch-target-h` on the two controls rather than relying on Button's own
    // TOUCH_FLOOR, which is `max-md:`-scoped: this bar renders all the way to
    // 1023.98px, so on an iPad in portrait the shared floor stops applying while
    // the bar is still the only route back to the list. SCRUM-1364's rule —
    // match the gate to where the component is actually visible.
    <div className="sticky top-0 z-30 flex shrink-0 items-center gap-2 border-b border-slate-200 bg-white px-3 py-2">
      <Button
        variant="ghost"
        size="sm"
        leftIcon={<ArrowLeft className="h-4 w-4" />}
        className="touch-target-h"
        onClick={() => setSelectedItem(null)}
      >
        Back
      </Button>
      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900">
        {selectedPatientName}
      </span>
      {selectedPatientId && (
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<PanelRightOpen className="h-4 w-4" />}
          className="touch-target-h"
          onClick={() => setDetailOpen(true)}
        >
          Details
        </Button>
      )}
    </div>
  ) : null;

  // One column at a time below `lg`. At `lg` and above both are shown, as today.
  const showList = !singleColumn || selectedItem == null;
  const showThread = !singleColumn || selectedItem != null;

  return (
    <OffCanvasRegion>
      {/*
        max-md: re-locks the viewport height that Layout.tsx deliberately releases
        below `md`. A list page is happy scrolling as a document; a conversation is
        not — it needs an internal scroll area with the composer pinned under it,
        and `h-full` alone resolves against an auto-height ancestor to nothing.
        3rem is the h-12 md:hidden header in Layout.tsx, which sits above <main>.

        The height term is the visual viewport, not `dvh`: `dvh` tracks collapsing
        browser chrome, not the on-screen keyboard. Same variable and same fallback
        as the `.h-visual-viewport` utility in index.css — spelled out here only
        because that utility has no subtraction and this page owes 3rem to the
        shell's header. The `var()` fallback is what applies wherever
        `useVisualViewportVars` attaches nothing: jsdom, the desktop-layout kill
        switch, or a browser with no visualViewport API.
      */}
      <div className="flex h-full min-h-0 flex-col bg-white max-md:h-[calc(var(--vv-height,100dvh)_-_3rem)] md:max-lg:h-[var(--vv-height,100%)]">
        {loadError && (
          <div className="border-b border-red-200 bg-red-50 px-6 py-3 text-sm text-red-700">
            {loadError}
          </div>
        )}

        <div className="flex min-h-0 flex-1 overflow-hidden">
          {showList && (
            <div
              className={singleColumn
                ? 'flex w-full min-w-0 min-h-0 flex-1 flex-col bg-slate-50'
                : 'flex w-80 shrink-0 min-h-0 flex-col border-r border-slate-200 bg-slate-50'}
            >
              {/*
                No search in the @Mentions view: that list cannot be searched, and a
                box that looked like it worked but ignored what was typed would read
                as "no matches". The list is short and ordered unread-first, so there
                is little to search for.
              */}
              {!mentionsView && (
                <div className="border-b border-slate-200 bg-white px-3 py-2">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          commitSearch();
                        } else if (e.key === 'Escape') {
                          setSearch('');
                          setCommittedSearch('');
                        }
                      }}
                      onBlur={commitSearch}
                      // Branch the string, not the CSS: the full hint does not fit a
                      // phone-width field and truncates mid-word.
                      placeholder={isPhone ? 'Search patients' : 'Search messages by patient (press Enter)'}
                      // enterKeyHint: this field commits on Enter, and a phone keyboard
                      // labels that key "go" unless told otherwise.
                      enterKeyHint="search"
                      className="h-9 touch-target-h w-full rounded-md border border-slate-200 bg-white pl-8 pr-3 text-sm placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200"
                    />
                  </div>
                </div>
              )}
              {mentionsView ? (
                <MentionsFilterBar
                  onMarkAllRead={() => { void handleMarkAllMentionsRead(); }}
                  disabled={markingAllRead || !hasUnreadMention}
                />
              ) : (
                <InboxFilterBar
                  folder={folder}
                  onChange={setFolder}
                  taggedToMe={taggedToMe}
                  onTaggedToMeChange={setTaggedToMe}
                />
              )}
              <div className="min-h-0 flex-1">
                <InboxList
                  items={items}
                  selectedId={selectedItem?.commLogId ?? null}
                  loading={loading}
                  loadingMore={loadingMore}
                  hasNext={hasNext}
                  activeSearch={committedSearch}
                  taggedToMe={taggedToMe}
                  mentionsEmptyMessage={mentionsView
                    ? facilityIds.length > 0
                      ? 'No mentions at the selected facilities.'
                      : 'No mentions yet. When a colleague tags you on a message or note, it shows up here.'
                    : undefined}
                  onSelect={handleSelectMessage}
                  onNameClick={handleNameClick}
                  onArchive={(entry) => { void handleArchiveMessage(entry); }}
                  onMerge={handleOpenMerge}
                  onLoadMore={handleLoadMore}
                />
              </div>
            </div>
          )}

          {/*
            The extra column wrapper exists only to host the narrow-width bar, so it
            is not rendered at ≥1024 — that tier keeps today's exact node tree.
          */}
          {showThread && (singleColumn ? (
            <div className="flex min-w-0 flex-1 flex-col bg-white">
              {threadBar}
              <div className="min-h-0 flex-1">{threadContent}</div>
            </div>
          ) : (
            <div className="min-w-0 flex-1 bg-white">{threadContent}</div>
          ))}

          {!singleColumn && detailPanel}
        </div>

        {singleColumn && (
          <OffCanvasPanel
            id="messages-detail"
            isOpen={detailOpen}
            onClose={() => setDetailOpen(false)}
            side="right"
            title="Patient details"
            size="wide"
          >
            {detailPanel}
          </OffCanvasPanel>
        )}

        <MessageMergeDialog
          isOpen={mergeTarget != null}
          entry={mergeTarget}
          onClose={() => setMergeTarget(null)}
          onMerged={handleMergeComplete}
        />
      </div>
    </OffCanvasRegion>
  );
}

function UnmatchedMessagePane({
  entry,
  onMerge,
}: {
  entry: CommLogEntry;
  onMerge: () => void;
}) {
  const senderRaw = entry.strActivity?.trim();
  const sender = senderRaw ? formatUnmatchedSender(senderRaw) : 'Unmatched sender';
  const senderContact = entry.strQuoteInfo?.trim() || '';
  const subject = (entry.subject || '').trim();
  const body = stripHtmlToText((entry.emailBody?.trim() || entry.smsMessage?.trim() || entry.message?.trim() || ''));

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-6 py-4">
        <div className="min-w-0">
          <p className="truncate text-base font-semibold text-slate-900">{sender}</p>
          {senderContact && (
            <p className="truncate text-sm text-slate-500">{senderContact}</p>
          )}
          <p className="mt-1 text-xs text-slate-400">
            {formatInboxTimestamp(entry.createdAt)}
            {' · '}
            <span className="rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-800">Unmatched</span>
          </p>
        </div>
        <button
          type="button"
          onClick={onMerge}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
        >
          <span className="inline-flex h-4 w-4 items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
              <circle cx="18" cy="18" r="3" />
              <circle cx="6" cy="6" r="3" />
              <path d="M6 21V9a9 9 0 0 0 9 9" />
            </svg>
          </span>
          Merge to patient
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
        {subject && (
          <p className="mb-3 text-sm font-semibold text-slate-700">{subject}</p>
        )}
        <div className="whitespace-pre-wrap break-words text-sm text-slate-800">
          {body || <span className="text-slate-400">No message content.</span>}
        </div>

        {entry.readHistory && entry.readHistory.length > 0 && (
          <div className="mt-6 flex flex-col gap-0.5 border-t border-slate-200 pt-4 text-xs text-slate-500">
            {entry.readHistory.map((event) => {
              // Straight from the raw entry, not the thread mapper, so the
              // helper is what keeps an unknown screen off this line.
              const screenLabel = readSourceLabel(event.source);
              return (
                <div key={`${event.userId}-${event.readAt}`}>
                  {formatReadTimestamp(event.readAt)} &mdash; Marked as read by {event.name}
                  {screenLabel && (
                    <>
                      {' '}
                      <span className="whitespace-nowrap">({screenLabel})</span>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-6 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          This message isn't linked to a patient yet. Click <span className="font-semibold">Merge to patient</span> above to associate it with a patient and (optionally) a plan.
        </div>
      </div>
    </div>
  );
}
